/**
 * Private offline cache (app layer, not the service worker).
 * Cache names are scoped to the signed-in user AND household: `sk-private:<uid>:<householdId>`
 * (account-level responses use `sk-private:<uid>:account`). Everything under `sk-private:` is
 * deleted on sign-out, account deletion and household switch so data never leaks across users
 * or households on a shared device.
 *
 * Races with in-flight work are closed by three rules:
 * 1. Every invalidation bumps `generation` synchronously, before any await. Work that started under an
 *    older generation may not write (cache or photo memory) and its results are treated as stale.
 * 2. All cache writes and deletions run through one serial queue. A write that passed its generation
 *    check is therefore always followed by the deletion queued by any later invalidation, so a cleared
 *    cache cannot be recreated by a response that was already in flight.
 * 3. Reads use `caches.match(…, { cacheName })`, which never creates a cache.
 * Writes are also bound to the active session (uid, and household once known).
 *
 * Offline limitation: invalidation runs in the browser. Revoking access or deleting data on the server
 * cannot erase what a device that stays fully offline has already cached; it is removed the next time
 * that device reaches the server (403/404 responses, the household list from /me) or signs out.
 */
export const PRIVATE_PREFIX = "sk-private:";
const ACCOUNT_SCOPE = "account";

const supported = () => typeof caches !== "undefined";

export function cacheNameFor(uid: string, path: string): string {
  return `${PRIVATE_PREFIX}${uid}:${scopeOf(path)}`;
}

/** Household id for household paths, otherwise "account". */
export function scopeOf(path: string): string {
  const match = /\/api\/v1\/households\/([^/?#]+)/.exec(path);
  return match ? match[1] : ACCOUNT_SCOPE;
}

function keyFor(path: string) {
  const origin = typeof location !== "undefined" ? location.origin : "http://localhost";
  return new Request(new URL(path, origin).toString());
}

// ---------- Session binding & generation ----------
interface PrivateSession {
  uid: string;
  /** Active household, or null while it is not known yet (any household of the user may be cached). */
  householdId: string | null;
}

let session: PrivateSession | null = null;
let generation = 0;
/** Bumped only when the signed-in account changes or ends (not on household switch). */
let accountEpoch = 0;
const listeners = new Set<() => void>();

/** Current invalidation generation. Capture it before starting async work, pass it to writes. */
export function privateGeneration(): number {
  return generation;
}

export function privateSession(): Readonly<PrivateSession> | null {
  return session;
}

/** Changes whenever the signed-in account changes or signs out; results fetched across it are stale. */
export function privateAccountEpoch(): number {
  return accountEpoch;
}

/** Subscribe to invalidations (for useSyncExternalStore). */
export function subscribePrivateGeneration(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** True while `gen` is still current and `uid` is the active session user. */
export function isCurrentPrivate(gen: number, uid: string | null): boolean {
  return gen === generation && session !== null && session.uid === uid;
}

function bump() {
  generation += 1;
  photoMemory.clear();
  for (const fn of listeners) fn();
}

function writable(uid: string, path: string, gen: number) {
  if (!isCurrentPrivate(gen, uid)) return false;
  const scope = scopeOf(path);
  return scope === ACCOUNT_SCOPE || session!.householdId === null || session!.householdId === scope;
}

// ---------- Serial write/delete queue ----------
let queue: Promise<unknown> = Promise.resolve();
function enqueue(op: () => Promise<void>): Promise<void> {
  const run = queue.then(op, op).catch(() => undefined);
  queue = run;
  return run;
}

async function deleteWhere(keep: (uid: string, scope: string) => boolean) {
  if (!supported()) return;
  try {
    const names = await caches.keys();
    await Promise.all(
      names
        .filter((n) => n.startsWith(PRIVATE_PREFIX))
        .filter((n) => {
          const [uid, scope] = n.slice(PRIVATE_PREFIX.length).split(":");
          return !keep(uid, scope);
        })
        .map((n) => caches.delete(n)),
    );
  } catch {
    // ignore
  }
}

// ---------- Session lifecycle (called by AuthProvider) ----------

/**
 * Bind the cache to the Firebase user reported at startup or after an account change. Caches of every
 * other account are deleted; the user's own caches are kept so a signed-in device can boot offline.
 * `null` (signed out) deletes everything.
 */
export function activatePrivateSession(uid: string | null): Promise<void> {
  if (uid === null) return endPrivateSession();
  if (session?.uid !== uid) {
    accountEpoch += 1;
    bump();
  }
  session = { uid, householdId: session?.uid === uid ? session.householdId : null };
  return enqueue(() => deleteWhere((owner) => owner === uid));
}

/** Sign-out / account deletion: stop all writes immediately, then delete every private cache. */
export function endPrivateSession(): Promise<void> {
  if (session) accountEpoch += 1;
  bump();
  session = null;
  return enqueue(() => deleteWhere(() => false));
}

/**
 * Record the active household. Switching from a known household to another (or any explicit switch,
 * `explicit`) invalidates in-flight work and deletes every private cache; the new household's cache is
 * rebuilt online. Learning the household for the first time after sign-in keeps the offline cache.
 */
export function setPrivateHousehold(uid: string, householdId: string | null, explicit = false): Promise<void> {
  if (!session || session.uid !== uid) return Promise.resolve();
  if (householdId === session.householdId) return Promise.resolve();
  const switching = explicit || session.householdId !== null;
  session = { uid, householdId };
  if (!switching) return Promise.resolve();
  bump();
  return enqueue(() => deleteWhere(() => false));
}

/** Delete household caches of `uid` for households the user no longer belongs to (from an online /me). */
export function retainPrivateHouseholds(uid: string, householdIds: string[], gen = generation): Promise<void> {
  if (!isCurrentPrivate(gen, uid)) return Promise.resolve();
  const keep = new Set(householdIds);
  for (const key of photoMemory.keys()) {
    const scope = key.split("\n")[1];
    if (scope !== ACCOUNT_SCOPE && !keep.has(scope)) photoMemory.delete(key);
  }
  return enqueue(() => deleteWhere((owner, scope) => owner === uid && (scope === ACCOUNT_SCOPE || keep.has(scope))));
}

/** Forget every cached entry for a whole household (e.g. the server now denies it). */
export function dropPrivateHousehold(uid: string, householdId: string): Promise<void> {
  for (const key of photoMemory.keys()) if (key.startsWith(`${uid}\n${householdId}\n`)) photoMemory.delete(key);
  return enqueue(async () => {
    if (supported()) await caches.delete(`${PRIVATE_PREFIX}${uid}:${householdId}`).catch(() => false);
  });
}

/** Forget one cached resource (the server says it is gone or no longer visible). */
export function dropPrivate(uid: string, path: string): Promise<void> {
  photoMemory.delete(photoKey(uid, path));
  return enqueue(async () => {
    if (!supported()) return;
    try {
      if (!(await caches.has(cacheNameFor(uid, path)))) return;
      const cache = await caches.open(cacheNameFor(uid, path));
      await cache.delete(keyFor(path));
    } catch {
      // ignore
    }
  });
}

/**
 * Deletes every private cache and invalidates in-flight work. The session binding is unchanged; prefer
 * endPrivateSession / setPrivateHousehold, which also update it.
 */
export function clearAllPrivateCaches(): Promise<void> {
  bump();
  return enqueue(() => deleteWhere(() => false));
}

// ---------- Reads & writes ----------

/**
 * Store a response for the session that started the request. `gen` must be captured when the request
 * started; stale or foreign-session writes are dropped.
 */
export function putPrivate(uid: string, path: string, response: Response, gen = generation): Promise<void> {
  if (!supported() || !writable(uid, path, gen)) return Promise.resolve();
  return enqueue(async () => {
    if (!writable(uid, path, gen)) return;
    try {
      const cache = await caches.open(cacheNameFor(uid, path));
      if (!writable(uid, path, gen)) return;
      await cache.put(keyFor(path), response);
    } catch {
      // Quota or private-mode failures only reduce offline coverage.
    }
  });
}

/** Cached response for the current session, or undefined (also when invalidated while reading). */
export async function matchPrivate(uid: string, path: string, gen = generation): Promise<Response | undefined> {
  if (!supported() || !isCurrentPrivate(gen, uid)) return undefined;
  try {
    const hit = await caches.match(keyFor(path), { cacheName: cacheNameFor(uid, path) });
    return hit && isCurrentPrivate(gen, uid) ? hit : undefined;
  } catch {
    return undefined;
  }
}

// ---------- Photos ----------

/** In-memory photo blobs for the current session, keyed by uid + household + src; cleared on every invalidation. */
export const photoMemory = new Map<string, Blob>();
const photoKey = (uid: string, src: string) => `${uid}\n${scopeOf(src)}\n${src}`;

const staleError = () => new DOMException("The session changed while this was loading", "AbortError");

/**
 * Photo bytes are immutable per id, so they are served memory -> private cache -> network, and stored
 * for offline use. Every step re-checks the generation; a load that crosses an invalidation rejects with
 * an AbortError and writes nothing.
 */
export async function loadPrivatePhoto(uid: string, src: string, signal: AbortSignal, fetchBlob: (src: string, signal: AbortSignal) => Promise<Blob>): Promise<Blob> {
  const gen = generation;
  if (!isCurrentPrivate(gen, uid)) throw staleError();
  const memo = photoMemory.get(photoKey(uid, src));
  if (memo) return memo;
  const hit = await matchPrivate(uid, src, gen);
  if (hit) {
    const blob = await hit.blob();
    if (!isCurrentPrivate(gen, uid) || signal.aborted) throw staleError();
    photoMemory.set(photoKey(uid, src), blob);
    return blob;
  }
  const blob = await fetchBlob(src, signal);
  if (!isCurrentPrivate(gen, uid)) throw staleError();
  if (writable(uid, src, gen)) photoMemory.set(photoKey(uid, src), blob);
  await putPrivate(uid, src, new Response(blob, { headers: { "Content-Type": blob.type || "image/webp" } }), gen);
  if (!isCurrentPrivate(gen, uid)) throw staleError();
  return blob;
}
